import axios from 'axios';
import { AlertCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Calendar } from '../../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Checkbox } from '../../../components/ui/checkbox';
import { useEffect, useState } from 'react';
import { StepCard } from '../StepCard';
import { CaretDownIcon } from '@phosphor-icons/react';

export function PersonalDetails({ onNext, onPrevious, data, onDataChange, phoneEditable = true }) {
  const [formData, setFormData] = useState({
    name: data?.name || '',
    email: data?.email || '',
    mobileNumber: data?.mobileNumber || '',
    // isWhatsAppNumber: data?.isWhatsAppNumber || false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [showPhoneMessage, setShowPhoneMessage] = useState(false);

  const handlePhoneClick = () => {
    if (!phoneEditable) {
      setShowPhoneMessage(true);
      setTimeout(() => setShowPhoneMessage(false), 3000);
    }
  };

  const formatDateToYYYYMMDD = (dateInput) => {
    if (!dateInput) return '';

    let date;
    if (dateInput instanceof Date) {
      date = dateInput;
    } else {
      date = new Date(dateInput);
    }

    if (isNaN(date.getTime())) {
      console.warn('Invalid date provided to formatDateToYYYYMMDD:', dateInput);
      return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseLocalDate = (yyyyMMdd) => {
    if (!yyyyMMdd) return undefined;
    const [year, month, day] = yyyyMMdd.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  useEffect(() => {
    const userProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
    if (userProfile.name) {
      setFormData((prev) => ({
        ...prev,
        name: userProfile.name,
        email: userProfile.email,
        mobileNumber: userProfile.mobileNumber?.slice(-10) || '',
        // isWhatsAppNumber: userProfile.isWhatsAppNumber || false,
      }));
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'This field is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.name)) {
      newErrors.name = 'Name can only contain alphabets';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Name should have at least 3 letters';
    } else if (formData.name.trim().length > 70) {
      newErrors.name = 'Name should have at most 70 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'This field is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (phoneEditable) {
      if (!formData.mobileNumber.trim()) {
        newErrors.mobileNumber = 'This field is required';
      } else if (!/^\d{10}$/.test(formData.mobileNumber)) {
        newErrors.mobileNumber = 'Please enter a valid 10-digit Indian mobile number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const updateData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        ...(phoneEditable && { mobileNumber: `${formData.mobileNumber}` }),
        // isWhatsAppNumber: formData.isWhatsAppNumber,
      };

      const response = await axios.patch(`/api/user/profile/`, updateData);

      if (response.status === 200) {
        localStorage.setItem('userProfile', JSON.stringify(response.data.data.user));
        onDataChange(updateData);
        onNext();
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setErrors({ submit: 'Failed to save data. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    const newErrors = { ...errors };

    if (field === 'name') {
      if (!value.trim()) {
        delete newErrors.name;
      } else if (!/^[a-zA-Z\s]+$/.test(value)) {
        newErrors.name = 'Name can only contain alphabets';
      } else if (value.trim().length < 3) {
        newErrors.name = 'Name should have at least 3 letters';
      } else if (value.trim().length > 70) {
        newErrors.name = 'Name should have at most 70 characters';
      } else {
        delete newErrors.name;
      }
    }

    if (field === 'email') {
      if (!value.trim()) {
        delete newErrors.email;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
        newErrors.email = 'Please enter a valid email address';
      } else {
        delete newErrors.email;
      }
    }

    if (field === 'mobileNumber' && phoneEditable) {
      if (!value.trim()) {
        delete newErrors.mobileNumber;
      } else if (!/^\d{10}$/.test(value)) {
        newErrors.mobileNumber = 'Please enter a valid 10-digit Indian mobile number';
      } else {
        delete newErrors.mobileNumber;
      }
    }

    setErrors(newErrors);
  };

  return (
    <StepCard>
      <div className="space-y-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-[var(--q3-neutral-default)] mb-2">Personal Details</h2>
          <p className="text-[var(--q3-neutral-light)] text-sm">Please enter your name and phone number to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-2">
          <div className="space-y-2">
            <div>
              <label className="block text-sm font-medium text-[var(--q3-neutral-default)] mb-2">Full Name</label>
              <Input
                type="text"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                  handleInputChange('name', value);
                }}
                className="h-12 rounded-full"
                required
              />
              {errors.name && (
                <div className="flex items-center gap-2 mt-1">
                  <AlertCircle className="h-4 w-4 text-[var(--q3-danger-normal)]" />
                  <span className="text-[var(--q3-danger-normal)] text-sm">{errors.name}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--q3-neutral-default)] mb-2">Email</label>
              <Input
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="h-12 rounded-full"
                required
              />
              {errors.email && (
                <div className="flex items-center gap-2 mt-1">
                  <AlertCircle className="h-4 w-4 text-[var(--q3-danger-normal)]" />
                  <span className="text-[var(--q3-danger-normal)] text-sm">{errors.email}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--q3-neutral-default)] mb-2">Phone Number</label>
              <div className="flex gap-2" onClick={handlePhoneClick}>
                <div className="flex items-center px-3 py-2 border border-[var(--q3-stroke-normal)] rounded-full bg-[var(--q3-surface-default)] min-w-[80px]">
                  <span className="text-sm">🇮🇳 +91</span>
                </div>
                <Input
                  type="tel"
                  placeholder="Enter your phone number"
                  value={formData.mobileNumber}
                  onChange={(e) => {
                    if (!phoneEditable) return;
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    handleInputChange('mobileNumber', value);
                  }}
                  className={`flex-1 h-12 rounded-full ${!phoneEditable && 'cursor-not-allowed'}`}
                  maxLength={10}
                  required={phoneEditable}
                  disabled={!phoneEditable}
                />
              </div>
              {phoneEditable && errors.mobileNumber && (
                <div className="flex items-center gap-2 mt-1">
                  <AlertCircle className="h-4 w-4 text-[var(--q3-danger-normal)]" />
                  <span className="text-[var(--q3-danger-normal)] text-sm">{errors.mobileNumber}</span>
                </div>
              )}
              {showPhoneMessage && !phoneEditable && (
                <div className="flex items-center gap-2 mt-1">
                  <AlertCircle className="h-4 w-4 text-[var(--q3-danger-normal)]" />
                  <span className="text-[var(--q3-danger-normal)] text-sm">
                    Changing your phone number is not permitted.
                  </span>
                </div>
              )}
            </div>
            {/* <div className="flex items-center space-x-2">
              <Checkbox
                id="whatsapp"
                checked={formData.isWhatsAppNumber}
                onCheckedChange={(checked) => handleInputChange('isWhatsAppNumber', checked === true)}
              />
              <label
                htmlFor="whatsapp"
                className="text-sm text-[var(--q3-neutral-default)] leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                I have WhatsApp on this number
              </label>
            </div> */}
          </div>

          {errors.submit && <div className="text-[var(--q3-danger-normal)] text-sm text-center">{errors.submit}</div>}

          <div className="flex flex-col sm:flex-row pt-4 gap-4 sm:gap-x-4">
            <Button
              type="button"
              variant="secondary"
              onClick={onPrevious}
              className="w-full sm:flex-shrink-0 sm:flex-grow-0 sm:basis-1/3 px-4 sm:px-8 rounded-full bg-[var(--q3-surface-default)] hover:bg-[var(--q3-surface-default)]/90 text-[var(--q3-neutral-default)] disabled:opacity-50 border border-[var(--q3-stroke-normal)]"
              disabled
            >
              Previous
            </Button>
            <Button
              type="submit"
              className="w-full sm:flex-shrink-0 sm:flex-grow sm:basis-2/3 bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)] disabled:opacity-50 px-4 sm:px-8 rounded-full"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save & Next'}
            </Button>
          </div>
        </form>
      </div>
    </StepCard>
  );
}
